/**
 * Runtime guard contra deploys com env vars erradas (build local de dev
 * apontando pra api de prod, etc).
 *
 * Detecta mismatch entre o hostname do site e o VITE_API_URL/VITE_TENANT_DOMAIN
 * usado no bundle. Se houver mismatch, lanca erro fatal antes do app montar
 * — melhor falhar visivelmente do que autenticar contra ambiente errado.
 */

interface EnvCheck {
  ok: boolean;
  reason?: string;
  details?: Record<string, string>;
}

/**
 * Mapeia hostname -> ambiente esperado.
 * - dev.campanhaflow.com.br | tenant.dev.campanhaflow.com.br -> dev
 * - staging.campanhaflow.com.br | tenant.staging.campanhaflow.com.br -> staging
 * - app.campanhaflow.com.br | tenant.campanhaflow.com.br | campanhaflow.com.br -> prod
 * - localhost -> qualquer (desenvolvimento local nao trava)
 */
function detectExpectedEnv(hostname: string): 'dev' | 'staging' | 'prod' | 'local' {
  if (hostname === 'localhost' || hostname.startsWith('127.') || hostname.startsWith('192.168.')) {
    return 'local';
  }
  if (hostname.endsWith('.staging.campanhaflow.com.br') || hostname === 'staging.campanhaflow.com.br') {
    return 'staging';
  }
  if (hostname.endsWith('.dev.campanhaflow.com.br') || hostname === 'dev.campanhaflow.com.br') {
    return 'dev';
  }
  return 'prod';
}

function detectApiEnv(apiUrl: string): 'dev' | 'staging' | 'prod' | 'unknown' {
  if (apiUrl.includes('api-dev.')) return 'dev';
  if (apiUrl.includes('api-staging.')) return 'staging';
  if (apiUrl.includes('api.campanhaflow.com.br')) return 'prod';
  return 'unknown';
}

export function checkEnv(): EnvCheck {
  const hostname = window.location.hostname;
  const apiUrl = import.meta.env.VITE_API_URL ?? '';

  const siteEnv = detectExpectedEnv(hostname);
  const apiEnv = detectApiEnv(apiUrl);

  // Local nao trava — desenvolvedor pode estar testando contra qualquer ambiente
  if (siteEnv === 'local') return { ok: true };

  if (apiEnv === 'unknown' || siteEnv !== apiEnv) {
    return {
      ok: false,
      reason: `Bundle apontando para ambiente errado. Site=${siteEnv}, API=${apiEnv}`,
      details: { hostname, apiUrl, siteEnv, apiEnv },
    };
  }
  return { ok: true };
}

/**
 * Renderiza tela de erro fatal e bloqueia o boot do app.
 * Chame antes de qualquer ReactDOM.render.
 */
export function blockBootIfEnvWrong(): void {
  const result = checkEnv();
  if (result.ok) return;

  const root = document.getElementById('root');
  if (!root) return;

  // Mensagem visivel, copiavel, evitando que o usuario sequer entre no app.
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;background:#fff;padding:24px;">
      <div style="max-width:560px;border:2px solid #dc2626;border-radius:12px;padding:24px;background:#fef2f2;">
        <h1 style="margin:0 0 12px;color:#991b1b;font-size:20px;">Configuracao de ambiente invalida</h1>
        <p style="margin:0 0 12px;color:#374151;font-size:14px;">${result.reason}</p>
        <p style="margin:0 0 8px;color:#6b7280;font-size:12px;">O bundle desse site foi gerado apontando para uma API de outro ambiente. Logar aqui poderia gerar tokens contra ambiente errado.</p>
        <pre style="background:#1f2937;color:#f9fafb;border-radius:6px;padding:12px;font-size:12px;overflow:auto;">${JSON.stringify(result.details, null, 2)}</pre>
        <p style="margin:12px 0 0;color:#6b7280;font-size:12px;">Solucao: rebuild com env vars corretas (use os scripts <code>build:dev</code> / <code>build:staging</code> / <code>build</code>) ou aguarde o pipeline rebuilder.</p>
      </div>
    </div>
  `;
  // Stop tudo: nao deixa o React montar.
  throw new Error(`[envGuard] ${result.reason} | ${JSON.stringify(result.details)}`);
}
